/**
 * Resolved relay-schedule timeline math for the site dashboard relay card.
 *
 * A relay schedule stores its on/off edges as either a fixed "HH:mm[:ss]"
 * time or a sun-relative type (sunset/sunrise ± a minute offset). This
 * module resolves those edges to concrete minute-of-day positions and, from
 * there, to the drawable on/off segment(s) the timeline strip renders and
 * the nearest upcoming transition the calendar-mode "next change" label
 * reads — the two dashboard surfaces that both need hardware-truth timing
 * off the same `relay.schedules` snapshot.
 *
 * Contains:
 * - resolveEdge — one schedule edge (fixed time or sun-relative) → minutes-of-day
 * - resolveActiveScheduleSpans — enabled schedules → per-schedule on/off segments
 * - findNextScheduleChange — nearest upcoming on/off transition after a given minute
 *
 * Returns plain JS — no JSX, no redux, no i18n.
 */
import { addMinutes } from 'date-fns';

const MINUTES_PER_DAY = 1440;

/**
 * Converts a Date to minutes elapsed since midnight (0–1439).
 *
 * @param {Date} date
 * @returns {number}
 */
export function minutesOfDay(date) {
  return date.getHours() * 60 + date.getMinutes();
}

/**
 * Resolves one schedule edge (on or off) to minutes-of-day (0–1440).
 *
 * Sun-relative types use the site's next_sunset / next_sunrise Date plus the
 * stored minute offset. Fixed times are parsed from "HH:mm[:ss]" strings.
 * Returns null when the required sun time is not available.
 *
 * @param {'sunset'|'sunrise'|string|null} type
 * @param {string|null} fixedTimeStr   "HH:mm" or "HH:mm:ss"
 * @param {number|null} offset         Minutes offset for sun-relative edges
 * @param {Date|null}   nextSunrise
 * @param {Date|null}   nextSunset
 * @returns {number|null}
 */
export function resolveEdge(type, fixedTimeStr, offset, nextSunrise, nextSunset) {
  if (type === 'sunset' || type === 'sunrise') {
    const base = type === 'sunset' ? nextSunset : nextSunrise;
    if (base == null) return null;
    return minutesOfDay(addMinutes(base, offset ?? 0));
  }
  if (fixedTimeStr != null) {
    const parts = fixedTimeStr.split(':');
    const h = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    if (!Number.isNaN(h) && !Number.isNaN(m)) {
      return h * 60 + m;
    }
  }
  return null;
}

/**
 * Builds the drawable segment(s) for one schedule's on/off window.
 *
 * Always-on is a single full-day segment. A normal window is one segment.
 * An overnight window (off <= on) wraps around midnight as two segments.
 * Either edge unresolved (sun time unavailable) yields no segment.
 *
 * @param {boolean} alwaysOn
 * @param {number|null} onMinutes
 * @param {number|null} offMinutes
 * @returns {{ fromMinutes: number, toMinutes: number }[]}
 */
function resolveScheduleSegments(alwaysOn, onMinutes, offMinutes) {
  if (alwaysOn) return [{ fromMinutes: 0, toMinutes: MINUTES_PER_DAY }];
  if (onMinutes == null || offMinutes == null) return [];
  if (offMinutes <= onMinutes) {
    return [
      { fromMinutes: onMinutes, toMinutes: MINUTES_PER_DAY },
      { fromMinutes: 0, toMinutes: offMinutes },
    ];
  }
  return [{ fromMinutes: onMinutes, toMinutes: offMinutes }];
}

/**
 * Resolves every enabled schedule to its on/off minute edges and drawable
 * segments. Disabled schedules never contribute a hardware-active span, so
 * they are excluded rather than returned with empty segments.
 *
 * @param {object[]} schedules   Raw relay.schedules rows (snake_case fields).
 * @param {Date|null} nextSunrise
 * @param {Date|null} nextSunset
 * @returns {{
 *   scheduleId: number,
 *   scheduleNumber: number,
 *   alwaysOn: boolean,
 *   onMinutes: number|null,
 *   offMinutes: number|null,
 *   segments: { fromMinutes: number, toMinutes: number }[],
 * }[]} Ordered by schedule_number.
 */
export function resolveActiveScheduleSpans(schedules, nextSunrise, nextSunset) {
  return (schedules ?? [])
    .filter(schedule => schedule.enabled ?? false)
    .slice()
    .sort((a, b) => a.schedule_number - b.schedule_number)
    .map(schedule => {
      const alwaysOn = schedule.always_on ?? false;
      const onMinutes = alwaysOn
        ? 0
        : resolveEdge(
            schedule.on_type ?? null,
            schedule.on_time ?? null,
            schedule.on_offset ?? null,
            nextSunrise,
            nextSunset,
          );
      const offMinutes = alwaysOn
        ? MINUTES_PER_DAY
        : resolveEdge(
            schedule.off_type ?? null,
            schedule.off_time ?? null,
            schedule.off_offset ?? null,
            nextSunrise,
            nextSunset,
          );
      return {
        scheduleId: schedule.id,
        scheduleNumber: schedule.schedule_number,
        alwaysOn,
        onMinutes,
        offMinutes,
        segments: resolveScheduleSegments(alwaysOn, onMinutes, offMinutes),
      };
    });
}

/**
 * Finds the nearest upcoming on/off transition across a relay's active
 * schedules, for the calendar-mode "next change" label. Always-on schedules
 * never transition and are skipped.
 *
 * @param {ReturnType<typeof resolveActiveScheduleSpans>} scheduleSpans
 * @param {number} nowMinutes   Current minute-of-day (0–1439) in the site's time zone.
 * @returns {{ minutes: number, isOn: boolean }|null} The nearest edge, or null
 *   when there is nothing to transition (no active schedules, or all always-on).
 */
export function findNextScheduleChange(scheduleSpans, nowMinutes) {
  let nearest = null;
  scheduleSpans.forEach(span => {
    if (span.alwaysOn) return;
    [
      { minutes: span.onMinutes, isOn: true },
      { minutes: span.offMinutes, isOn: false },
    ].forEach(edge => {
      if (edge.minutes == null) return;
      const minutesUntil = (edge.minutes - nowMinutes + MINUTES_PER_DAY) % MINUTES_PER_DAY;
      if (nearest == null || minutesUntil < nearest.minutesUntil) {
        nearest = { minutes: edge.minutes, isOn: edge.isOn, minutesUntil };
      }
    });
  });
  return nearest ? { minutes: nearest.minutes, isOn: nearest.isOn } : null;
}
